// material-ui
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

// project imports
import { Notification, notificationFilterOptions } from './data';
import NotificationItem from './NotificationItem';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import SimpleBar from 'components/third-party/SimpleBar';

interface NotificationContentProps {
  notifications: Notification[];
  totalCount: number;
  unreadCount: number;
  loading: boolean;
  error?: string | null;
  filter: 'all' | 'unread' | 'mentions';
  setFilter: (value: 'all' | 'unread' | 'mentions') => () => void;
  onNotificationClick: (notification: Notification) => void;
  onRefresh: () => void;
}

// ==============================|| NOTIFICATION - CONTENT ||============================== //

export default function NotificationContent({
  notifications,
  totalCount,
  unreadCount,
  loading,
  error,
  filter,
  setFilter,
  onNotificationClick,
  onRefresh
}: NotificationContentProps) {
  return (
    <MainCard
      title={<Typography variant="h5">Notification</Typography>}
      sx={{ border: 'none', borderRadius: 0, height: '100vh', '& .MuiCardHeader-root': { p: 2.25 } }}
      content={false}
      secondary={
        <Link href="#" underline="hover" typography="caption" sx={{ textTransform: 'none' }} onClick={onRefresh}>
          Refresh
        </Link>
      }
    >
      {/* Notification Filter Chips */}
      <Stack
        direction="row"
        sx={{ py: 1.5, px: 2.5, gap: 1.25, alignItems: 'center', borderBottom: '1px solid', borderBottomColor: 'divider' }}
      >
        {notificationFilterOptions.map((opt, index) => (
          <Chip
            key={index}
            label={opt.label}
            color={filter === opt.key ? 'primary' : 'secondary'}
            variant="combined"
            clickable
            onClick={setFilter(opt.key)}
            sx={{
              flexDirection: 'row-reverse',
              '& .MuiChip-avatar': { width: 20, height: 20, ml: -0.5, mr: 0.5 },
              ...(filter !== opt.key && { border: '1px solid transparent !important' })
            }}
            {...{
              ...(opt.key === 'all' && {
                avatar: <Avatar size="xs">{totalCount}</Avatar>
              }),
              ...(opt.key === 'unread' && {
                avatar: <Avatar size="xs">{unreadCount}</Avatar>
              })
            }}
          />
        ))}
      </Stack>

      {/* Notification List */}
      <Box>
        <SimpleBar sx={{ overflowX: 'hidden', height: 'calc(100vh - 175px)', minHeight: 420 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Box sx={{ px: 2.5, py: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ px: 2.5, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No notifications found.
              </Typography>
            </Box>
          ) : (
            <List component="nav" disablePadding>
              {notifications.map((notification: Notification) => (
                <NotificationItem key={notification.id} notification={notification} onClick={() => onNotificationClick(notification)} />
              ))}
            </List>
          )}
        </SimpleBar>
      </Box>

      {/* Footer Button */}
      <Stack sx={{ py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button sx={{ alignSelf: 'center' }}>View All Notifications</Button>
      </Stack>
    </MainCard>
  );
}
